import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Loader2, Sparkles, ArrowRight, Crown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import Navigation from '../components/Navigation';
import WhoLikedYou from '../components/WhoLikedYou';
import PremiumModal from '../components/PremiumModal';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { formatDistanceToNow } from 'date-fns';

import API_URL from '../config/api';

export default function Matches() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPremium, setShowPremium] = useState(false);
  const [premiumTrigger, setPremiumTrigger] = useState('');

  useEffect(() => {
    fetchMatches();
  }, []);

  const fetchMatches = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/matches`, { withCredentials: true });
      setMatches(response.data.matches);
    } catch (err) {
      toast.error('Failed to load matches');
    } finally {
      setLoading(false);
    }
  };

  const getPhotoUrl = (profile) => {
    if (profile?.primary_photo?.photo_id) {
      return `${API_URL}/api/photos/${profile.primary_photo.photo_id}?auth=placeholder`;
    }
    if (profile?.google_picture) {
      return profile.google_picture;
    }
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(profile?.name || 'U')}&background=6E1A24&color=fff&size=100`;
  };

  const handleUpgradeClick = (reason) => {
    setPremiumTrigger(reason);
    setShowPremium(true);
  };

  const handleUpgradeSuccess = async () => {
    await refreshUser();
    // Refresh the page to show updated content
    window.location.reload();
  };


  const newMatches = useMemo(() => matches.filter(m => !m.last_message), [matches]);
  const activeMatches = useMemo(() => matches.filter(m => m.last_message), [matches]);

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Navigation />
      
      <div className="pt-24 pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-3xl font-serif font-medium text-[#2D2D2A]">{t('matches.title')}</h1>
            {user?.current_plan === 'free' && (
              <Button
                onClick={() => handleUpgradeClick('default')}
                className="rounded-full bg-gradient-to-r from-[#D4AF37] to-[#C97A63] text-white text-xs font-medium px-3 py-1.5 h-auto hover:opacity-90 transition-opacity"
                data-testid="matches-upgrade-btn"
              >
                <Crown className="w-3.5 h-3.5 mr-1" />
                {t('common.upgrade')}
              </Button>
            )}
          </div>
          
          {/* Who Liked You Section */}
          <WhoLikedYou onUpgradeClick={handleUpgradeClick} />
          
          {loading ? (
            <div className="h-[60vh] flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-[#6E1A24] animate-spin" />
            </div>
          ) : matches.length === 0 ? (
            <div className="h-[60vh] flex flex-col items-center justify-center text-center px-4">
              {/* Animated hearts */}
              <div className="relative w-32 h-32 mb-8">
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-24 h-24 rounded-full bg-[#6E1A24]/10 flex items-center justify-center">
                    <Heart className="w-12 h-12 text-[#6E1A24]" />
                  </div>
                </div>
                <div className="absolute top-0 right-0 w-10 h-10 rounded-full bg-[#C97A63]/20 flex items-center justify-center animate-pulse">
                  <Heart className="w-5 h-5 text-[#C97A63]" />
                </div>
                <div className="absolute bottom-2 left-0 w-8 h-8 rounded-full bg-[#E6D5B8]/50 flex items-center justify-center animate-pulse" style={{ animationDelay: '0.5s' }}>
                  <Heart className="w-4 h-4 text-[#C97A63]" />
                </div>
              </div>
              
              <h2 className="text-2xl font-serif font-medium text-[#2D2D2A] mb-3">{t('matches.no_matches_title')}</h2>
              <p className="text-[#6C6C67] mb-8 max-w-sm leading-relaxed">
                {t('matches.no_matches_subtitle')}
              </p>
              
              <Link to="/discover" className="group">
                <Button className="bg-[#6E1A24] text-white rounded-full px-10 py-4 font-semibold hover:bg-[#8A2331] hover:shadow-xl transition-all flex items-center gap-2" data-testid="discover-link">
                  {t('matches.start_swiping')}
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              
              {/* Tips section */}
              <div className="mt-12 p-6 bg-white rounded-2xl border border-[#E5E3DB] max-w-md">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#E6D5B8]/50 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#6E1A24]" />
                  </div>
                  <p className="font-medium text-[#2D2D2A]">{t('matches.tips_title')}</p>
                </div>
                <ul className="space-y-3 text-sm text-[#6C6C67] text-left">
                  <li className="flex items-start gap-2">
                    <span className="text-[#6E1A24]">•</span>
                    {t('matches.tip1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#6E1A24]">•</span>
                    {t('matches.tip2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[#6E1A24]">•</span>
                    {t('matches.tip3')}
                  </li>
                </ul>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* New matches section */}
              {newMatches.length > 0 && (
                <div className="mb-6">
                  <p className="text-sm font-medium text-[#6C6C67] mb-3">{t('matches.new_matches')}</p>
                  <div className="flex gap-4 overflow-x-auto pb-2 -mx-4 px-4 no-scrollbar">
                    {newMatches.map(match => (
                      <button
                        key={match.match_id}
                        onClick={() => navigate(`/chat/${match.match_id}`)}
                        className="flex flex-col items-center gap-2 flex-shrink-0"
                        data-testid={`new-match-${match.match_id}`}
                      >
                        <div className="relative">
                          <div className="w-20 h-20 rounded-full overflow-hidden ring-2 ring-[#6E1A24] ring-offset-2">
                            <img 
                              src={getPhotoUrl(match.user)}
                              alt={match.user?.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-[#6E1A24] rounded-full flex items-center justify-center">
                            <Heart className="w-3 h-3 text-white" fill="white" />
                          </div>
                        </div>
                        <span className="text-sm font-medium text-[#2D2D2A] max-w-[80px] truncate">
                          {match.user?.nickname || match.user?.name}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Messages section */}
              {activeMatches.length > 0 && (
                <p className="text-sm font-medium text-[#6C6C67] mb-3">{t('matches.messages')}</p>
              )}
              
              {activeMatches.map(match => (
                <button
                  key={match.match_id}
                  onClick={() => navigate(`/chat/${match.match_id}`)}
                  className="w-full bg-white border border-[#E5E3DB] rounded-2xl p-4 flex items-center gap-4 hover:border-[#6E1A24]/30 hover:shadow-md transition-all text-left"
                  data-testid={`match-${match.match_id}`}
                >
                  <div className="w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img 
                      src={getPhotoUrl(match.user)}
                      alt={match.user?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium text-[#2D2D2A] truncate">
                        {match.user?.nickname || match.user?.name}
                      </h3>
                      {match.user?.is_verified && (
                        <span className="text-xs bg-[#2D6A4F]/10 text-[#2D6A4F] px-2 py-0.5 rounded-full font-medium">
                          {t('common.verified')}
                        </span>
                      )}
                    </div>
                    
                    <p className="text-sm text-[#6C6C67] truncate">
                      {match.last_message.sender_id === user?.user_id ? 'You: ' : ''}
                      {match.last_message.content}
                    </p>
                  </div>
                  
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-xs text-[#6C6C67]">
                      {formatDistanceToNow(new Date(match.last_message.created_at), { addSuffix: true })}
                    </span>
                    <MessageCircle className="w-5 h-5 text-[#6E1A24]" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Premium Modal */}
      <PremiumModal
        isOpen={showPremium}
        onClose={() => setShowPremium(false)}
        onUpgradeSuccess={handleUpgradeSuccess}
        currentPlan={user?.current_plan || 'free'}
        triggerReason={premiumTrigger}
      />
    </div>
  );
}
